/**
 * QUANDO LA CHROME SI COMPRIME ALLO SCROLL — la decisione, pura, perche' un test la possa esercitare.
 *
 * Luca, I-386 (04/10/2026): «quando scrollo lista tornei (o lista battaglie) abbiamo l'ottimo effetto di
 * minimizzare i bottoni. Voglio che la minimizzazione avvenga appena scrollo la lista, in questo momento
 * c'e' un micro movimento della lista prima della minimizzazione».
 *
 * 🔴 Misurato il 04/10 (app vera in chromium, tocco emulato, riga 0 della lista frame per frame): con la
 * vecchia soglia `y > 24` la lista scorreva **25-33 px a griglia APERTA** prima che la compressione partisse.
 * Il micro movimento era la soglia, non una transizione in ritardo.
 *
 * ⚠️ Abbassare la soglia a 1 px da sola NON basta: la riapertura era `y < 8`, e con compressione a `y = 1`
 * la prima riapre subito la seconda, a sfarfallio. L'isteresi sta quindi nella DIREZIONE, non in una zona morta:
 *   · si COMPRIME scorrendo verso il basso (`y > 0` e `y` in aumento);
 *   · si RIAPRE risalendo (`y < 8` e `y` in calo), ma solo se si e' scesi di almeno `RISALITA_MINIMA_PX` dal punto
 *     piu' basso toccato da compressa — o si e' in cima (`y <= 0`). Un tremore del dito nei primi pixel non e' un
 *     ritorno in cima: senza questa clausola, un `y` che fa 3 → 2 → 3 riapre e richiude la griglia.
 * Il rimbalzo di iOS in cima (`y < 0`, poi `y` che risale a 0) non comprime: serve `y > 0`.
 *
 * ⛔ NON cambia lo slittamento della lista oltre il dito mentre la griglia si chiude (138 px a 390×844, 116 a
 * 360×640, ~300 ms): la griglia sta nel flusso, e togliere altezza sopra la lista la sposta. E' geometria, non
 * soglia: v. la PR di tma-orcb-33-scroll.
 */

/** Sotto questa `y` una chrome compressa puo' riaprirsi (come prima: 8 px). */
export const SOGLIA_RIAPERTURA_PX = 8;

/** Di quanto si deve risalire dal punto piu' basso, a chrome compressa, per contare come «ritorno in cima». */
export const RISALITA_MINIMA_PX = 4;

/**
 * Non comprimere se il contenuto e' appena piu' alto dello schermo: comprimendo si libera spazio, il browser
 * azzera lo scroll perche' non c'e' piu' nulla da scorrere, l'azzeramento riapre la chrome e il contenuto torna a
 * sforare — un rimbalzo a ogni tentativo (v. il commento in `AppShell`). Sta sopra lo spazio recuperabile (~138 px).
 */
export const SCORRIBILE_MINIMO_PX = 240;

/** Cio' che si ricorda di UNO scroller fra un evento di scroll e il successivo. */
export interface ScrollVisto {
  /** L'ultima `scrollTop` vista. */
  y: number;
  /** La `y` piu' alta vista da quando la chrome e' compressa (o dall'ultima decisione). */
  picco: number;
}

export type DecisioneScroll = 'comprimi' | 'riapri' | null;

/**
 * Aggiorna lo stato dello scroller con la `y` appena letta e dice cosa fare con la chrome.
 * Muta `visto`: e' lo stato di quello scroller, tenuto da chi chiama (una `WeakMap` per elemento).
 *
 * @param visto       lo stato di questo scroller (`{ y: 0, picco: 0 }` la prima volta)
 * @param y           `scrollTop` adesso
 * @param compressa   la chrome e' compressa adesso
 * @param scorribile  `scrollHeight - clientHeight` dello scroller
 */
export function decidiCompressione(
  visto: ScrollVisto,
  y: number,
  compressa: boolean,
  scorribile: number,
): DecisioneScroll {
  const verso = Math.sign(y - visto.y);
  visto.y = y;
  if (y > visto.picco) visto.picco = y;

  if (!compressa && y > 0 && verso > 0 && scorribile > SCORRIBILE_MINIMO_PX) {
    visto.picco = y;
    return 'comprimi';
  }
  if (compressa && y < SOGLIA_RIAPERTURA_PX && verso < 0 && (y <= 0 || visto.picco - y >= RISALITA_MINIMA_PX)) {
    visto.picco = y;
    return 'riapri';
  }
  return null;
}
